// ── Welle D3c: Inhalte der Standard-Widgets (Radar, Lerngruppen, Mails, Termine, Mein Tag,
//    Erzählen, Woche) im neuen Look. Jede Zeile/Zahl führt per Klick an ihr Ziel
//    (bestehende Sprungziele aus U2/U6b); Ladefehler zeigen „Konnte nicht geladen werden“.
import React, { useEffect, useRef, useState } from 'react';
import { MIcon } from '../../../lib/micon';
import { apiPatch, zeigeApiFehler } from '../../../lib/api';
import { toast } from '../../../lib/toast';
import { useSchueler } from '../../../contexts/SchuelerContext';
import { ampelLabel, fzEntsch, fzUnen } from '../../../lib/utils';
import { PROFIL_ZIEL } from '../../../lib/insight-ziel';
import {
  globalNav, oeffneKlassenAktionen, oeffneMailVonAussen, openAusflugDetail, oeffneTerminBearbeiten,
} from '../../../lib/nav';
import type { KlasseFachEintrag, NavItem, ProfilZiel } from '../../../types';
import { LdSheet } from '../../../components/ui/LdSheet';
import { TaskErstellenModal } from '../../../components/tasks/TaskErstellenModal';
import { MitDaten, setzeDaten, useLader, ladeNeu } from './lader';
import { useWidgetGroesse, WidgetGroesse } from './groesse';

const anzahl = (g: WidgetGroesse, pc: (h: number, w: number) => number, klein: number, gross: number) =>
  g.geraet === 'pc' ? pc(g.h, g.w) : g.w >= 2 ? gross : klein;
const kuerzel = (name: string) => name.split(/[\s.@_-]+/).filter(Boolean).slice(0, 2).map(t => t[0]?.toUpperCase()).join('') || '?';

// ════════════════════════════════════════════════════════════════════════════
// Radar
// ════════════════════════════════════════════════════════════════════════════
type Insight = { id: string; typ: string; prioritaet: 1 | 2 | 3; titel: string; subtext: string; link: string; schueler_id?: number; mail_id?: number };
type Muster = { id: number; name: string; klasse_name: string; anzahl: number; text: string | null };
type Aktion = { id: number; klasse_id: number; titel: string; faellig_am: string; klasse_name: string; erledigt_anzahl: number; gesamt_anzahl: number };
type Ton = 'rot' | 'amber' | 'violett' | 'grau';
type RadarZeile = { key: string; ton: Ton; name: string; grund: string; ist68?: boolean; grundKurz?: string; oeffnen: () => void; task?: { titel: string; id: string; schuelerId: number | null; klasseId: number | null } };
const TON_RANG: Record<Ton, number> = { rot: 0, amber: 1, violett: 2, grau: 3 };

export function useRadarZeilen() {
  const { schuelerListe, setOpenProfilId } = useSchueler();
  const auf = useLader<{ insights: Insight[] }>('/api/dashboard/aufmerksamkeit');
  const mu = useLader<{ auffaellig: Muster[] }>('/api/dashboard/klassenbuch-muster');
  const ak = useLader<Aktion[]>('/api/aktionen/faellig');
  const kl = useLader<KlasseFachEintrag[]>('/api/meine-klassen');
  const profil = (id: number, ziel: ProfilZiel) => () => { setOpenProfilId(id, ziel); globalNav('schueler'); };
  const aktionOeffnen = (a?: Aktion) => {
    if (!a) { oeffneKlassenAktionen(null); return; }
    const aktiv = !kl.daten?.length || kl.daten.some(k => Number(k.klasse_id) === Number(a.klasse_id));
    if (!aktiv) { toast('Diese Aktion gehört zu einer archivierten Klasse.', 'info'); return; }
    oeffneKlassenAktionen(a.klasse_id, a.id);
  };
  const zeilen: RadarZeile[] = [];
  const schonDrin = new Set<number>();
  for (const i of auf.daten?.insights ?? []) {
    const [kopf, rest] = i.titel.includes(' — ') ? i.titel.split(' — ') : [i.titel, ''];
    const ton: Ton = i.typ === 'mail_prio_kontakt' ? 'violett' : i.prioritaet === 1 ? 'rot' : i.prioritaet === 2 ? 'violett' : 'grau';
    if (i.schueler_id) schonDrin.add(i.schueler_id);
    zeilen.push({
      key: i.id, ton, name: kopf, grund: [rest, i.subtext].filter(Boolean).join(' · '),
      // Welle K6: §68 am PC als roter Hinweis unter dem Grund
      ...(i.typ === 'fehlzeit_rot' ? { ist68: true, grundKurz: [rest, i.subtext?.split(' · ')[0]].filter(Boolean).join(', ') } : {}),
      oeffnen: i.schueler_id ? profil(i.schueler_id, PROFIL_ZIEL[i.typ] ?? 'ueberblick')
        : i.mail_id ? () => oeffneMailVonAussen(i.mail_id!)
        : i.typ === 'aktion_faellig' ? () => aktionOeffnen(ak.daten?.find(a => `aktion_${a.id}` === i.id))
        : () => globalNav(i.link as NavItem),
      task: { titel: i.titel, id: i.id, schuelerId: i.schueler_id ?? null, klasseId: i.typ === 'aktion_faellig' ? (ak.daten?.find(a => `aktion_${a.id}` === i.id)?.klasse_id ?? null) : null },
    });
  }
  // §68-Fälle nach den Schwellen der Lehrkraft (Client) — falls der Server sie noch nicht meldet
  for (const s of schuelerListe) {
    if (schonDrin.has(s.id)) continue;
    if (ampelLabel(s.fehlzeiten.stunden_gesamt, fzEntsch(s)) === '§68 HSchG') {
      schonDrin.add(s.id);
      zeilen.push({ key: `p68_${s.id}`, ton: 'rot', name: s.name, grund: `${fzUnen(s)} Std. unentschuldigt, §68-Schwelle überschritten`, ist68: true, grundKurz: `${fzUnen(s)} Std. unentschuldigt`, oeffnen: profil(s.id, 'fehlzeiten') });
    }
  }
  // Fehlendes Attest (amber)
  for (const s of schuelerListe) {
    if ((s.attest_offen ?? 0) > 0)
      zeilen.push({ key: `attest_${s.id}`, ton: 'amber', name: s.name, grund: s.attest_offen === 1 ? 'Attest fehlt' : `${s.attest_offen} Atteste fehlen`, oeffnen: profil(s.id, 'fehlzeiten') });
  }
  // Wiederkehrend fehlend (Klassenbuch-Muster): höchstens 2, nach Dringlichkeit einsortiert
  for (const m of (mu.daten?.auffaellig ?? []).slice(0, 2)) {
    zeilen.push({ key: `muster_${m.id}`, ton: 'violett', name: m.name, grund: `${m.text ? m.text[0].toUpperCase() + m.text.slice(1) : `${m.anzahl}× gefehlt in 3 Wochen`} · ${m.klasse_name}`, oeffnen: profil(m.id, 'fehlzeiten') });
  }
  zeilen.sort((a, b) => TON_RANG[a.ton] - TON_RANG[b.ton]);
  // Laden/Fehler hängen am Haupt-Lader; das Muster ist eine Ergänzung (Fehler dort nur still)
  return { g: auf, zeilen };
}

function RadarListe({ zeilen, onTask, pc }: { zeilen: RadarZeile[]; onTask?: (z: RadarZeile) => void; pc?: boolean }) {
  return (
    <ul className="ld-wl ld-radar">
      {zeilen.map(z => (
        <li key={z.key} className="ld-radar__zeile">
          <button type="button" className="ld-radar__btn" onClick={z.oeffnen}>
            <span className={`ld-punkt ld-punkt--${z.ton}`} aria-hidden="true" />
            <span className="ld-radar__text"><b>{z.name}</b><small>{pc && z.grundKurz ? z.grundKurz : z.grund}</small>{pc && z.ist68 && <span className="ld-radar__68">über §68-Schwelle</span>}</span>
          </button>
          {onTask && z.task && (
            <button type="button" className="ld-radar__task" aria-label="Als Aufgabe speichern" title="Als Aufgabe speichern" onClick={() => onTask(z)}>
              <MIcon name="add_task" size={17} />
            </button>
          )}
        </li>
      ))}
    </ul>
  );
}

export function RadarWidget() {
  const gr = useWidgetGroesse();
  const { g, zeilen } = useRadarZeilen();
  const [alleOffen, setAlleOffen] = useState(false);
  const [task, setTaskRoh] = useState<RadarZeile['task'] | null>(null);
  const n = anzahl(gr, h => h * 2, 2, 4);
  // Welle U6b (in D3c übernommen): Zurück-Taste schließt den Aufgaben-Dialog statt Heute zu verlassen
  const taskOffenRef = useRef(false);
  function taskOeffnen(t: RadarZeile['task'] | null) {
    if (!t) return;
    window.history.pushState({ ldModal: 'radar-task', ldScreen: 'home' }, '', window.location.pathname);
    taskOffenRef.current = true;
    setTaskRoh(t);
  }
  function taskSchliessen() {
    taskOffenRef.current = false;
    setTaskRoh(null);
    if (window.history.state?.ldModal === 'radar-task') window.history.back();
  }
  useEffect(() => {
    const zurueck = () => { if (taskOffenRef.current) { taskOffenRef.current = false; setTaskRoh(null); } };
    window.addEventListener('popstate', zurueck);
    return () => window.removeEventListener('popstate', zurueck);
  }, []);
  return (
    <MitDaten g={g} zeilen={2}>
      {() => zeilen.length === 0 ? (
        <div className="ld-wleer"><MIcon name="check_circle" size={22} /><span>Alles im grünen Bereich. Neue Auffälligkeiten erscheinen hier.</span></div>
      ) : (
        <div className="ld-wspalte">
          <RadarListe zeilen={zeilen.slice(0, n)} pc={gr.geraet === 'pc'} />
          {/* immer anbieten: dort steht auch „Als Aufgabe speichern“ (M9) */}
          <button type="button" className="ld-wmehr" onClick={() => setAlleOffen(true)}>
            {zeilen.length === 1 ? '1 Hinweis öffnen' : `Alle ${zeilen.length} Hinweise`}
          </button>
          <LdSheet open={alleOffen} onClose={() => setAlleOffen(false)} title="Auf deinem Radar" maxWidth={560}>
            <div className="ld-wsheet">
              <RadarListe zeilen={zeilen} onTask={z => { setAlleOffen(false); window.setTimeout(() => taskOeffnen(z.task ?? null), 250); }} />
            </div>
          </LdSheet>
          {task && (
            <TaskErstellenModal open onClose={taskSchliessen} onErstellt={taskSchliessen}
              initialSchuelerId={task.schuelerId} initialKlasseId={task.klasseId} initialTitel={task.titel}
              quelle="radar" quelle_ref_id={task.id} quelle_ref_typ="radar" />
          )}
        </div>
      )}
    </MitDaten>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Lerngruppen
// ════════════════════════════════════════════════════════════════════════════
export function LerngruppenWidget() {
  const gr = useWidgetGroesse();
  const { schuelerListe, setInitialKlasseFilter } = useSchueler();
  const kl = useLader<KlasseFachEintrag[]>('/api/meine-klassen');
  const ak = useLader<Aktion[]>('/api/aktionen/faellig');
  return (
    <MitDaten g={kl} zeilen={2}>
      {(klassen) => {
        type Gruppe = { klasse_id: number; klasse_name: string; istKL: boolean; faecher: string[] };
        const gruppen: Gruppe[] = [];
        for (const k of klassen) {
          let x = gruppen.find(y => y.klasse_id === k.klasse_id);
          if (!x) { x = { klasse_id: k.klasse_id, klasse_name: k.klasse_name, istKL: false, faecher: [] }; gruppen.push(x); }
          if (k.rolle === 'klassenleiter') x.istKL = true;
          if (!x.faecher.includes(k.fach)) x.faecher.push(k.fach);
        }
        if (!gruppen.length) return (
          <div className="ld-wleer"><MIcon name="groups" size={22} /><span>Noch keine Klasse eingerichtet.</span>
            <button type="button" className="ld-wlink" onClick={() => globalNav('einstellungen')}>Klasse einrichten</button></div>
        );
        const n = anzahl(gr, (h, w) => w * h, 1, 4);
        const zeigen = (k: string) => { setInitialKlasseFilter(k); globalNav('schueler'); };
        return (
          <div className={`ld-lg2 ld-lg2--${gr.geraet === 'pc' ? Math.min(gr.w, 4) : 1}`}>
            {gruppen.slice(0, n).map(gp => {
              const ks = schuelerListe.filter(s => Number(s.klasse_id) === Number(gp.klasse_id));
              const unentsch = ks.reduce((sum, s) => sum + fzUnen(s), 0);
              const offen = (ak.daten ?? []).filter(a => Number(a.klasse_id) === Number(gp.klasse_id)).length;
              const labels = ks.map(s => ampelLabel(s.fehlzeiten.stunden_gesamt, fzEntsch(s)));
              const pill = labels.includes('§68 HSchG') ? { mod: 'rot', text: '§68' }
                : ks.some(s => (s.attest_offen ?? 0) > 0) ? { mod: 'amber', text: 'Attest offen' }
                : labels.includes('Achtung') ? { mod: 'violett', text: 'Achtung' }
                : gr.geraet === 'pc' ? { mod: 'ruhig', text: 'alles ruhig' } : { mod: 'ok', text: 'Alles ok' };
              return (
                <div key={gp.klasse_id} className="ld-lg2__kachel">
                  <div className="ld-lg2__kopfzeile">
                    <button type="button" className="ld-lg2__kopf" onClick={() => zeigen(gp.klasse_name)}>
                      <span className={`ld-lg2__badge${gp.istKL ? ' ist-kl' : ''}`}>{gp.klasse_name}</span>
                      <span className="ld-lg2__info"><b>Klasse {gp.klasse_name}</b><small>{gp.istKL ? 'Klassenleitung, ' : ''}{gp.faecher.join(', ')}</small></span>
                    </button>
                    <span className={`ld-pille ld-pille--${pill.mod} ld-lg2__pille-oben`}>{pill.text}</span>
                  </div>
                  <div className="ld-lg2__zahlen">
                    <button type="button" onClick={() => zeigen(gp.klasse_name)}><b>{ks.length}</b><small>{gr.geraet === 'pc' ? 'Kinder' : 'Schüler'}</small></button>
                    <button type="button" className={unentsch > 0 ? 'ist-rot' : ''} onClick={() => globalNav('fehlzeiten')}><b>{unentsch}</b><small>{gr.geraet === 'pc' ? 'Std. unentsch.' : 'unentsch.'}</small></button>
                    <button type="button" onClick={() => oeffneKlassenAktionen(gp.klasse_id)}><b>{offen}</b><small>offen</small></button>
                    <span className={`ld-pille ld-pille--${pill.mod} ld-lg2__pille-unten`}>{pill.text}</span>
                  </div>
                </div>
              );
            })}
          </div>
        );
      }}
    </MitDaten>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Mails
// ════════════════════════════════════════════════════════════════════════════
type MailKurz = {
  id: number; subject: string; sender_name: string | null; sender_email: string; prio_effektiv: 'hoch' | 'mittel' | 'niedrig' | null;
  absender?: { typ: 'eltern' | 'kontakt' | 'beides' | null; kontakt: { rolle: string | null; name: string | null } | null; kinder: { vorname: string; klasse: string }[] };
};
type Triage = { neu_heute?: number; neueste?: MailKurz[]; anzahlGesamt: number };

export function MailsWidget() {
  const gr = useWidgetGroesse();
  const g = useLader<Triage>('/api/mail/triage');
  const n = anzahl(gr, h => h === 1 ? 2 : 6, 2, 4);
  return (
    <MitDaten g={g} zeilen={2}>
      {(t) => {
        const liste = (t.neueste ?? []).slice(0, n);
        return (
          <div className="ld-wspalte">
            <button type="button" className="ld-gross-zahl" onClick={() => globalNav('mails')}>
              {t.neu_heute ?? 0}<span>neu heute</span>
            </button>
            {liste.length === 0 ? (
              <p className="ld-wtext">Alles gelesen. Neue Mails erscheinen hier.</p>
            ) : (
              <ul className="ld-wl">
                {liste.map(m => {
                  const rolle = m.absender?.kontakt?.rolle ?? '';
                  const istLeitung = /schulleit|rektor|direktor/i.test(rolle);
                  const istEltern = m.absender?.typ === 'eltern' || m.absender?.typ === 'beides';
                  const name = m.absender?.kontakt?.name || m.sender_name || m.sender_email.split('@')[0];
                  const titel = istLeitung ? `Schulleitung: ${m.subject}`
                    : istEltern && m.absender?.kinder?.[0] ? `${m.subject} · ${m.absender.kinder[0].klasse}` : m.subject;
                  return (
                    <li key={m.id}>
                      <button type="button" className="ld-mail-zeile" onClick={() => oeffneMailVonAussen(m.id)} title={`${name}: ${m.subject}`}>
                        <span className={`ld-kuerzel${istLeitung ? ' ist-dunkel' : istEltern ? ' ist-tint' : ''}`}>{kuerzel(name)}</span>
                        <span className="ld-mail-zeile__text">{gr.geraet === 'pc' ? <><b>{name}</b> · {istLeitung ? m.subject : titel}</> : titel}</span>
                        {m.prio_effektiv === 'hoch' && <MIcon name="star" size={15} fill className="ld-stern" label="Wichtig" />}
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        );
      }}
    </MitDaten>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Termine
// ════════════════════════════════════════════════════════════════════════════
type Termin = { id: number; datum: string; datum_ende?: string | null; titel: string; kategorie: string; beschreibung?: string | null; klasse_name?: string | null };
const KAT_LABEL: Record<string, string> = { konferenz: 'Konferenz', eltern: 'Eltern', klassenfahrt: 'Klassenfahrt', zeugnis: 'Zeugnis', ferien: 'Ferien', ausflug: 'Ausflug', klassenarbeit: 'Klassenarbeit', sonstiges: 'Termin' };

export function TermineWidget() {
  const gr = useWidgetGroesse();
  const g = useLader<Termin[]>('/api/schultermine');
  const n = anzahl(gr, h => h === 1 ? 2 : 5, 2, 4);
  return (
    <MitDaten g={g} zeilen={2}>
      {(alle) => {
        const heute = new Date().toLocaleDateString('sv-SE');
        const kommend = alle.filter(t => (t.datum_ende ?? t.datum).slice(0, 10) >= heute).sort((a, b) => a.datum.localeCompare(b.datum)).slice(0, n);
        if (!kommend.length) return (
          <div className="ld-wleer"><MIcon name="event_available" size={22} /><span>Keine Termine in nächster Zeit.</span>
            <button type="button" className="ld-wlink" onClick={() => globalNav('kalender')}>Termin eintragen</button></div>
        );
        return (
          <ul className="ld-wl">
            {kommend.map(t => {
              const d = new Date(t.datum.slice(0, 10) + 'T12:00:00');
              const uhr = t.datum.length > 10 ? t.datum.slice(11, 16) : null;
              const zusatz = [uhr, t.klasse_name, KAT_LABEL[t.kategorie] ?? null].filter(Boolean).slice(0, 2).join(' · ');
              return (
                <li key={t.id}>
                  <button type="button" className="ld-termin" onClick={() => t.kategorie === 'ausflug' ? openAusflugDetail(t.id, 'home') : oeffneTerminBearbeiten(t.id)}>
                    <span className="ld-termin__datum"><b>{d.getDate()}</b><small>{d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')}</small></span>
                    <span className="ld-termin__text"><strong>{t.titel}</strong><span>{zusatz}</span></span>
                  </button>
                </li>
              );
            })}
          </ul>
        );
      }}
    </MitDaten>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Mein Tag (Aufgaben, direkt abhakbar)
// ════════════════════════════════════════════════════════════════════════════
type Task = { id: number; titel: string; erledigt: number; erledigt_am: string | null; faellig_am: string | null; prioritaet?: string | null };
type Tasks = { tasks: Task[]; gruppen: Record<'ueberfaellig' | 'heute' | 'diese_woche' | 'spaeter' | 'undatiert' | 'erledigt', Task[]> };
const TASKS = '/api/tasks';

// Fällig bis heute (Browser-Datum) + heute erledigte; ohne Datum zählt nicht zu „Mein Tag“
function tagesAufgaben(t: Tasks, geradeAbgehakt: Set<number> = new Set()) {
  const heute = new Date().toLocaleDateString('sv-SE');
  const heuteUtc = new Date().toISOString().slice(0, 10);
  const faellig = (x: Task) => !!x.faellig_am && x.faellig_am.slice(0, 10) <= heute;
  const offen = t.tasks.filter(x => !x.erledigt && faellig(x));
  // heute erledigte — und in dieser Sitzung abgehakte bleiben sichtbar, damit man sie zurücknehmen kann
  const erledigtHeute = t.tasks.filter(x => x.erledigt && faellig(x) && (String(x.erledigt_am ?? '').slice(0, 10) === heuteUtc || geradeAbgehakt.has(x.id)));
  return { offen, liste: [...offen, ...erledigtHeute] };
}

export function MeinTagZusatz() {
  const g = useLader<Tasks>(TASKS);
  if (!g.daten) return null;
  const n = tagesAufgaben(g.daten).offen.length;
  return <button type="button" className="ld-w__link ld-w__zaehler" onClick={() => globalNav('meinTag')}>{n} offen</button>;
}

// Welle K6: Fälligkeit kurz — „heute“, „gestern“, sonst TT.MM.
function faelligText(f: string, heute: string) {
  const d = f.slice(0, 10);
  if (d === heute) return 'heute';
  const x = new Date(heute + 'T12:00:00'); x.setDate(x.getDate() - 1);
  if (d === x.toLocaleDateString('sv-SE')) return 'gestern';
  return `${d.slice(8, 10)}.${d.slice(5, 7)}.`;
}

export function MeinTagWidget() {
  const gr = useWidgetGroesse();
  const g = useLader<Tasks>(TASKS);
  const n = anzahl(gr, h => h === 1 ? 3 : h === 2 ? 8 : 12, 3, 6);
  const [geradeAbgehakt, setGeradeAbgehakt] = useState<Set<number>>(() => new Set());
  async function umschalten(t: Task) {
    setGeradeAbgehakt(alt => new Set(alt).add(t.id));
    const neu = t.erledigt ? 0 : 1;
    const aendern = (x: Task) => x.id === t.id ? { ...x, erledigt: neu, erledigt_am: neu ? new Date().toISOString() : null } : x;
    setzeDaten<Tasks>(TASKS, alt => alt ? { ...alt, tasks: alt.tasks.map(aendern) } : alt!);
    try { await apiPatch(`/api/tasks/${t.id}`, { erledigt: !!neu }); void ladeNeu(TASKS); }
    catch (e) { zeigeApiFehler(e, 'Aufgabe konnte nicht gespeichert werden'); void ladeNeu(TASKS); }
  }
  return (
    <MitDaten g={g} zeilen={3}>
      {(t) => {
        const { liste } = tagesAufgaben(t, geradeAbgehakt);
        if (!liste.length) return (
          <div className="ld-wleer"><MIcon name="task_alt" size={22} /><span>Für heute nichts offen.</span>
            <button type="button" className="ld-wlink" onClick={() => globalNav('meinTag')}>Aufgabe anlegen</button></div>
        );
        const pc = gr.geraet === 'pc';
        const heute = new Date().toLocaleDateString('sv-SE');
        const ul = (
          <ul className="ld-wl ld-aufgaben">
            {liste.slice(0, n).map(a => (
              <li key={a.id} className={a.erledigt ? 'ist-erledigt' : ''}>
                <button type="button" role="checkbox" aria-checked={!!a.erledigt} className="ld-aufgabe" onClick={() => umschalten(a)}>
                  <span className="ld-aufgabe__box">{a.erledigt ? <MIcon name="check" size={13} /> : null}</span>
                  <span className="ld-aufgabe__text">{a.titel}</span>
                  {pc && a.faellig_am && !a.erledigt && (
                    <span className={`ld-aufgabe__faellig${a.faellig_am.slice(0, 10) < heute ? ' ist-ueber' : ''}`}>{faelligText(a.faellig_am, heute)}</span>
                  )}
                </button>
              </li>
            ))}
          </ul>
        );
        if (!pc) return ul;
        return (
          <div className="ld-wspalte">
            {ul}
            <button type="button" className="ld-wmehr" onClick={() => globalNav('meinTag')}>Alle Aufgaben</button>
          </div>
        );
      }}
    </MitDaten>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Erzählen (Welle K6: hell, früher dunkles „Schnell-Diktat“)
// ════════════════════════════════════════════════════════════════════════════
export function DiktatWidget() {
  return (
    <button type="button" className="ld-ez" onClick={() => window.dispatchEvent(new CustomEvent('ld-universaldiktat', { detail: { seitenleiste: true } }))}>
      <span className="ld-ez__mikro"><MIcon name="mic" size={22} /></span>
      <b className="ld-ez__titel">Erzählen</b>
      <small className="ld-ez__satz">Einfach sagen, was war. Die KI sortiert es ein.</small>
      <kbd className="ld-ez__taste">Strg+D</kbd>
    </button>
  );
}

// ════════════════════════════════════════════════════════════════════════════
// Deine Woche
// ════════════════════════════════════════════════════════════════════════════
type Woche = { von: string; bis: string; tage: { datum: string; schultag: boolean; aktivitaet: number }[]; fehlzeiten_erfasst: number; notizen: number; klassenbuch_eintraege: number; mails_beantwortet: number };
const WOCHE = '/api/heute/woche';

export function WocheZusatz() {
  const g = useLader<Woche>(WOCHE);
  if (!g.daten) return null;
  const v = new Date(g.daten.von + 'T12:00:00'), b = new Date(g.daten.bis + 'T12:00:00');
  const text = v.getMonth() === b.getMonth()
    ? `${v.getDate()}. bis ${b.getDate()}. ${b.toLocaleDateString('de-DE', { month: 'long' })}`
    : `${v.getDate()}. ${v.toLocaleDateString('de-DE', { month: 'long' })} bis ${b.getDate()}. ${b.toLocaleDateString('de-DE', { month: 'long' })}`;
  return <span className="ld-w__zeitraum">{text}</span>;
}

export function WocheWidget() {
  const gr = useWidgetGroesse();
  const g = useLader<Woche>(WOCHE);
  return (
    <MitDaten g={g} zeilen={2}>
      {(w) => {
        const heute = new Date().toLocaleDateString('sv-SE');
        const max = Math.max(1, ...w.tage.map(t => t.aktivitaet));
        const pc = gr.geraet === 'pc';
        const zahlen: { n: number; label: string; ziel: NavItem }[] = pc ? [
          // Welle K6: am PC alle vier Zahlen, die /api/heute/woche liefert
          { n: w.fehlzeiten_erfasst, label: w.fehlzeiten_erfasst === 1 ? 'Fehlzeit erfasst' : 'Fehlzeiten erfasst', ziel: 'fehlzeiten' },
          { n: w.notizen, label: w.notizen === 1 ? 'Notiz geschrieben' : 'Notizen geschrieben', ziel: 'notizen' },
          { n: w.klassenbuch_eintraege, label: w.klassenbuch_eintraege === 1 ? 'Stunde eingetragen' : 'Stunden eingetragen', ziel: 'klassenbuch' },
          { n: w.mails_beantwortet, label: w.mails_beantwortet === 1 ? 'Mail beantwortet' : 'Mails beantwortet', ziel: 'mails' },
        ] : [
          { n: w.fehlzeiten_erfasst, label: 'Fehlzeiten erfasst', ziel: 'fehlzeiten' },
          { n: w.notizen, label: 'Notizen', ziel: 'notizen' },
          { n: w.mails_beantwortet, label: 'Mails beantwortet', ziel: 'mails' },
        ];
        return (
          <div className="ld-woche2">
            <div className="ld-woche2__tage" role="list" aria-label="Aktivität je Tag">
              {w.tage.map(t => {
                const d = new Date(t.datum + 'T12:00:00');
                const zukunft = t.datum > heute;
                const hoehe = t.schultag ? Math.max(6, Math.round(64 * t.aktivitaet / max)) : 0;
                return (
                  <div key={t.datum} role="listitem" className={`ld-woche2__tag${t.datum === heute ? ' ist-heute' : ''}${zukunft ? ' ist-zukunft' : ''}`}
                    aria-label={`${d.toLocaleDateString('de-DE', { weekday: 'long' })}: ${t.schultag ? `${t.aktivitaet} Einträge` : 'schulfrei'}`}>
                    {t.schultag ? <span className="ld-woche2__balken" style={{ height: zukunft ? 6 : hoehe }} /> : <span className="ld-woche2__frei">frei</span>}
                    <small>{d.toLocaleDateString('de-DE', { weekday: 'short' }).replace('.', '')}</small>
                  </div>
                );
              })}
            </div>
            <div className="ld-woche2__zahlen">
              {zahlen.map(z => (
                <button key={z.label} type="button" onClick={() => globalNav(z.ziel)}><b>{z.n}</b><small>{z.label}</small></button>
              ))}
            </div>
          </div>
        );
      }}
    </MitDaten>
  );
}
